import { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Truck, Package, CheckCircle, Download, MapPin, Home } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const ShippingDialog = ({ isOpen, onClose, kitRequest, onSuccess }) => {
  const [step, setStep] = useState(1); // 1: Check rates, 2: Select courier, 3: Generate label
  const [loading, setLoading] = useState(false);
  const [couriers, setCouriers] = useState([]);
  const [sortedCouriers, setSortedCouriers] = useState([]);
  const [sortBy, setSortBy] = useState('price'); // price, delivery_time
  const [selectedCourier, setSelectedCourier] = useState(null);
  const [deliveryAddressType, setDeliveryAddressType] = useState('center'); // 'center' or 'student'
  const [centerAddress, setCenterAddress] = useState(null);
  const [studentAddress, setStudentAddress] = useState(null);
  const [shippingData, setShippingData] = useState({
    pickup_postcode: '400001',
    delivery_postcode: '',
    weight: 0.5,
    delivery_address: ''
  });
  const [shipmentResult, setShipmentResult] = useState(null);

  useEffect(() => {
    if (kitRequest && kitRequest.shipment_id) {
      setStep(3); // Already has shipment
    }
  }, [kitRequest]);

  // Fetch center and student addresses
  useEffect(() => {
    const fetchAddresses = async () => {
      if (kitRequest) {
        try {
          const token = localStorage.getItem('token');
          
          // Fetch center address
          if (kitRequest.center_id) {
            const centerResponse = await axios.get(
              `${API}/api/centers/${kitRequest.center_id}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (centerResponse.data) {
              setCenterAddress({
                address: centerResponse.data.full_address || centerResponse.data.address || '',
                pincode: centerResponse.data.postal_code || ''
              });
            }
          }
          
          // Fetch student address
          if (kitRequest.student_id) {
            const studentResponse = await axios.get(
              `${API}/api/users/${kitRequest.student_id}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (studentResponse.data) {
              setStudentAddress({
                address: studentResponse.data.full_address || studentResponse.data.address || '',
                pincode: studentResponse.data.postal_code || ''
              });
            }
          }
          
          // Set initial delivery address based on teacher's preference
          const preferredType = kitRequest.preferred_delivery_address || 'center';
          setDeliveryAddressType(preferredType);
          
        } catch (error) {
          console.error('Error fetching addresses:', error);
        }
      }
    };
    fetchAddresses();
  }, [kitRequest]);
  
  // Update shipping data when address type changes
  useEffect(() => {
    if (deliveryAddressType === 'center' && centerAddress) {
      setShippingData(prev => ({
        ...prev,
        delivery_postcode: centerAddress.pincode,
        delivery_address: centerAddress.address
      }));
    } else if (deliveryAddressType === 'student' && studentAddress) {
      setShippingData(prev => ({
        ...prev,
        delivery_postcode: studentAddress.pincode,
        delivery_address: studentAddress.address
      }));
    }
  }, [deliveryAddressType, centerAddress, studentAddress]);

  // Sort couriers when data changes or sort option changes
  useEffect(() => {
    if (couriers.length > 0) {
      const sorted = [...couriers].sort((a, b) => {
        if (sortBy === 'price') {
          return (a.rate || 0) - (b.rate || 0);
        } else if (sortBy === 'delivery_time') {
          return parseInt(a.estimated_delivery_days || 999) - parseInt(b.estimated_delivery_days || 999);
        }
        return 0;
      });
      setSortedCouriers(sorted);
    }
  }, [couriers, sortBy]);

  const handleCheckRates = async () => {
    if (!shippingData.delivery_postcode) {
      toast.error('Please enter delivery postcode');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/shiprocket/check-serviceability?pickup_postcode=${shippingData.pickup_postcode}&delivery_postcode=${shippingData.delivery_postcode}&weight=${shippingData.weight}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.data && response.data.data.available_courier_companies) {
        setCouriers(response.data.data.available_courier_companies);
        setStep(2);
      } else {
        toast.error('No couriers available for this route');
      }
    } catch (error) {
      console.error('Error checking rates:', error);
      toast.error('Failed to fetch courier rates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrder = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/shiprocket/create-order?kit_request_id=${kitRequest.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.shipment_id) {
        setShipmentResult(response.data);
        toast.success('Shipping order created successfully!');
        // Move to courier selection
        await handleCheckRates();
      }
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error(error.response?.data?.detail || 'Failed to create shipping order');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignCourier = async () => {
    if (!selectedCourier) {
      toast.error('Please select a courier');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const shipment_id = shipmentResult?.shipment_id || kitRequest.shipment_id;
      
      const response = await axios.post(
        `${API}/api/shiprocket/assign-courier?shipment_id=${shipment_id}&courier_id=${selectedCourier.id}&kit_request_id=${kitRequest.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.awb_code) {
        toast.success(`Courier assigned! AWB: ${response.data.awb_code}`);
        setStep(3);
        onSuccess?.();
      } else {
        // Handle cases where API returns 200 but assignment failed
        const errorMsg = response.data.message || 'Failed to assign courier';
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Error assigning courier:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to assign courier';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateLabel = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const shipment_id = shipmentResult?.shipment_id || kitRequest.shipment_id;
      
      const response = await axios.post(
        `${API}/api/shiprocket/generate-label`,
        { shipment_ids: [shipment_id] },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.label_url) {
        window.open(response.data.label_url, '_blank');
        toast.success('Shipping label generated!');
      }
    } catch (error) {
      console.error('Error generating label:', error);
      toast.error('Failed to generate label');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Shiprocket Shipping</DialogTitle>
        </DialogHeader>

        {/* Step 1: Check Rates */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-900 font-semibold">Step 1: Create Shipping Order</p>
              <p className="text-xs text-blue-700 mt-1">Select delivery address and check courier rates</p>
            </div>

            {kitRequest && (
              <div className="bg-gray-50 p-4 rounded-lg border">
                <p className="text-sm font-semibold text-gray-700">Kit Request Details:</p>
                <p className="text-xs text-gray-600 mt-1">Student: {kitRequest.student_name}</p>
                <p className="text-xs text-gray-600">Center: {kitRequest.center_name}</p>
                {kitRequest.preferred_delivery_address && (
                  <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded">
                    <p className="text-xs text-amber-800">
                      <span className="font-semibold">Teacher's Preference:</span> Deliver to {kitRequest.preferred_delivery_address === 'center' ? 'Center Address' : 'Student Address'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Address Selection */}
            <div>
              <Label className="text-base font-semibold mb-3 block">Choose Delivery Address</Label>
              <div className="space-y-3">
                <div 
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    deliveryAddressType === 'center' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setDeliveryAddressType('center')}
                >
                  <div className="flex items-start space-x-3">
                    <input 
                      type="radio" 
                      checked={deliveryAddressType === 'center'}
                      onChange={() => setDeliveryAddressType('center')}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">Deliver to Center Address</p>
                      {centerAddress && (
                        <div className="mt-2 text-sm text-gray-600">
                          <p>{centerAddress.address}</p>
                          <p className="font-semibold mt-1">Pincode: {centerAddress.pincode}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div 
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    deliveryAddressType === 'student' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setDeliveryAddressType('student')}
                >
                  <div className="flex items-start space-x-3">
                    <input 
                      type="radio" 
                      checked={deliveryAddressType === 'student'}
                      onChange={() => setDeliveryAddressType('student')}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">Deliver to Student Address</p>
                      {studentAddress && (
                        <div className="mt-2 text-sm text-gray-600">
                          <p>{studentAddress.address}</p>
                          <p className="font-semibold mt-1">Pincode: {studentAddress.pincode}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <Label>Delivery Pincode (Auto-filled)</Label>
              <Input
                value={shippingData.delivery_postcode}
                onChange={(e) => setShippingData({ ...shippingData, delivery_postcode: e.target.value })}
                placeholder="Auto-filled based on address selection"
                className="bg-gray-50"
              />
            </div>

            <div>
              <Label>Weight (kg)</Label>
              <Input
                type="number"
                step="0.1"
                value={shippingData.weight}
                onChange={(e) => setShippingData({ ...shippingData, weight: parseFloat(e.target.value) })}
              />
              <p className="text-xs text-gray-500 mt-1">Default: 0.5 kg</p>
            </div>

            <Button
              onClick={handleCreateOrder}
              disabled={loading || !shippingData.delivery_postcode}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {loading ? 'Creating...' : 'Create Order & Check Rates'}
            </Button>
          </div>
        )}

        {/* Step 2: Select Courier */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-sm text-green-900 font-semibold">Step 2: Select Courier Partner</p>
              <p className="text-xs text-green-700 mt-1">Compare couriers and choose the best option</p>
            </div>

            {/* Sort Options */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 font-medium">Sort by:</span>
              <Button
                variant={sortBy === 'price' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSortBy('price')}
                className={sortBy === 'price' ? 'bg-blue-600' : ''}
              >
                Price (Low to High)
              </Button>
              <Button
                variant={sortBy === 'delivery_time' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSortBy('delivery_time')}
                className={sortBy === 'delivery_time' ? 'bg-blue-600' : ''}
              >
                Delivery Time
              </Button>
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {sortedCouriers.map((courier) => (
                <div
                  key={courier.id}
                  onClick={() => setSelectedCourier(courier)}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedCourier?.id === courier.id
                      ? 'border-blue-500 bg-blue-50 shadow-md'
                      : 'border-gray-200 hover:border-blue-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-bold text-gray-900">{courier.courier_name}</p>
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                          courier.is_surface 
                            ? 'bg-amber-100 text-amber-700' 
                            : 'bg-sky-100 text-sky-700'
                        }`}>
                          {courier.is_surface ? 'Surface' : 'Air'}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-green-600">₹{courier.rate}</p>
                      <p className="text-xs text-gray-500">{courier.cod ? 'COD Available' : 'Prepaid'}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="bg-white p-2 rounded border border-gray-100">
                      <p className="text-xs text-gray-500">Expected Pickup</p>
                      <p className="font-semibold text-gray-800">{courier.suppress_date || 'N/A'}</p>
                    </div>
                    <div className="bg-white p-2 rounded border border-gray-100">
                      <p className="text-xs text-gray-500">Estimated Delivery</p>
                      <p className="font-semibold text-gray-800">{courier.etd || 'N/A'}</p>
                    </div>
                    <div className="bg-white p-2 rounded border border-gray-100">
                      <p className="text-xs text-gray-500">Delivery Time</p>
                      <p className="font-semibold text-blue-600">{courier.estimated_delivery_days || 'N/A'} days</p>
                    </div>
                    <div className="bg-white p-2 rounded border border-gray-100">
                      <p className="text-xs text-gray-500">RTO Charges</p>
                      <p className="font-semibold text-gray-800">₹{courier.rto_charges || 0}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <Button
              onClick={handleAssignCourier}
              disabled={loading || !selectedCourier}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              {loading ? 'Assigning...' : 'Assign Courier & Generate AWB'}
            </Button>
          </div>
        )}

        {/* Step 3: Generate Label */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <p className="text-sm text-purple-900 font-semibold">Step 3: Download Shipping Label</p>
              <p className="text-xs text-purple-700 mt-1">Courier assigned! Generate and download the shipping label</p>
            </div>

            {kitRequest.awb_code && (
              <div className="bg-white p-4 border rounded-lg">
                <Label className="text-sm text-gray-600">AWB Number:</Label>
                <p className="text-lg font-bold text-gray-900">{kitRequest.awb_code}</p>
                <Label className="text-sm text-gray-600 mt-2">Courier:</Label>
                <p className="text-base font-semibold text-gray-800">{kitRequest.courier_name}</p>
              </div>
            )}

            <Button
              onClick={handleGenerateLabel}
              disabled={loading}
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              <Download className="w-4 h-4 mr-2" />
              {loading ? 'Generating...' : 'Generate & Download Label'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShippingDialog;
